/**
 * Step Call Flow
 *
 * A `call_flow` step runs another flow at its position in the chain, and the
 * child runs as its own `workflow_runs` row — so a `call_flow` step's own
 * `errorMessage` only ever carries a summary ("Flow \"X\" failed: ..."). This
 * makes the child run reachable (a link built from `childRunId` +
 * `childWorkflowId`) and legible (distinguishes `not-run`, an authoring
 * problem that never started the child, from `child-failed`, the child
 * running and failing its own assertions — and flags a `warnedFailure`, where
 * `onFailure: 'warn'` kept the step green even though the child failed).
 *
 * @module FlowDesigner/runs/StepCallFlow
 */
import React from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Warning, XCircle } from '@phosphor-icons/react';
import { Text, Tooltip, TooltipTrigger } from '@chatsdet/ui';
import type { WorkflowRunStepMetadataDto } from '../hooks/useWorkflowRun';

export function StepCallFlow({
  metadataJson,
}: {
  metadataJson: WorkflowRunStepMetadataDto | null;
}) {
  if (!metadataJson?.isCallFlowStep) return null;

  const {
    childRunId,
    childWorkflowId,
    childWorkflowName,
    failureClass,
    missingOutputs = [],
    publishedOutputs = [],
    warnedFailure,
  } = metadataJson;

  const hasChildLink = Boolean(childRunId && childWorkflowId);

  if (
    !hasChildLink &&
    !failureClass &&
    !warnedFailure &&
    missingOutputs.length === 0 &&
    publishedOutputs.length === 0
  )
    return null;

  return (
    <div className="ml-4 mt-1.5 space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        {failureClass === 'not-run' && (
          <TooltipTrigger delay={0}>
            <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-default">
              <Warning size={10} weight="fill" />
              Configuration error
            </span>
            <Tooltip>The child flow never started — fix the step's setup, not the child.</Tooltip>
          </TooltipTrigger>
        )}
        {failureClass === 'child-failed' && (
          <TooltipTrigger delay={0}>
            <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 cursor-default">
              <XCircle size={10} weight="fill" />
              Child flow failed
            </span>
            <Tooltip>The child flow ran and failed its own assertions.</Tooltip>
          </TooltipTrigger>
        )}
        {warnedFailure && (
          <TooltipTrigger delay={0}>
            <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 cursor-default">
              <Warning size={10} weight="fill" />
              Child flow failed (warned)
            </span>
            <Tooltip>
              This step is green because "On failure" is set to warn, but the child flow actually
              failed.
            </Tooltip>
          </TooltipTrigger>
        )}
        {childRunId && childWorkflowId && (
          <Link
            to="/flows/$workflowId"
            params={{ workflowId: childWorkflowId }}
            search={{ runId: childRunId }}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            View child run{childWorkflowName ? `: ${childWorkflowName}` : ''}
            <ArrowRight size={12} />
          </Link>
        )}
      </div>
      {publishedOutputs.length > 0 && (
        <Text size="xs" className="text-muted-foreground">
          Published to later steps: {publishedOutputs.join(', ')}
        </Text>
      )}

      {missingOutputs.length > 0 && (
        <Text size="xs" className="text-amber-600 dark:text-amber-400">
          Declared but not produced: {missingOutputs.join(', ')}
        </Text>
      )}
    </div>
  );
}
